// src/app/page.tsx
"use client";

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import LandingPage from "@/components/LandingPage";
import { User } from 'firebase/auth';
import FirebaseService, { Assistant, Template, Tag } from '@/lib/firebase';
import type { UserState, AssistantDisplay } from '@/types';
import LoginDialog from "@/components/LoginDialog";
import { SidebarProvider } from "@/components/ui/sidebar";
import { useAssistantCache } from '@/contexts/AssistantCacheContext';

// Helper to map Assistant to AssistantDisplay
const mapAssistantToDisplay = (assistant: Assistant): AssistantDisplay => ({
  assistantId: assistant.assistantId,
  templateId: assistant.templateId,
  name: assistant.name,
  avatar: assistant.avatar,
  ownerId: assistant.ownerId,
  ownerEmail: assistant.ownerEmail,
  initialMessage: assistant.initialMessage,
  initialInstructions: assistant.initialInstructions,
  initialDocuments: assistant.initialDocuments || [],
  createdAt: assistant.createdAt ? new Date(assistant.createdAt) : undefined,
  updatedAt: assistant.updatedAt ? new Date(assistant.updatedAt) : undefined,
  shareUrl: `/assistant/${assistant.assistantId}`,
  usageCount: assistant.usageCount || 0,
  lastAccessedAt: assistant.lastAccessedAt ? new Date(assistant.lastAccessedAt) : undefined,
  tools: assistant.tools || [],
  toolConfigs: assistant.toolConfigs || {},
  accessControl: assistant.accessControl || { type: 'domain' }
});

export default function Page() {
  const router = useRouter();
  const [userState, setUserState] = useState<UserState>('not-logged-in');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<Template[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // Use the shared cache from layout
  const { assistants: cachedAssistants, loading: isCacheLoading } = useAssistantCache();

  // Auth Listener Effect
  useEffect(() => {
    const unsubscribe = FirebaseService.onAuthStateChange((user) => {
      setCurrentUser(user);
      setUserState(user ? 'receiver' : 'not-logged-in');
    });
    return () => unsubscribe();
  }, []);

  // Redirect logged-in users to /list
  useEffect(() => {
    if (currentUser) {
      router.push('/list');
    }
  }, [currentUser, router]);

  // Filter cached assistants into user and public
  const userAssistants = useMemo(() => {
    if (!currentUser) return [];
    return cachedAssistants
      .filter(a => a.ownerId === currentUser.uid)
      .map(mapAssistantToDisplay);
  }, [cachedAssistants, currentUser]);

  const publicAssistants = useMemo(() => {
    return cachedAssistants
      .filter(a => a.accessControl?.type === 'public')
      .map(mapAssistantToDisplay);
  }, [cachedAssistants]);

  // Load tags and templates (only these need separate fetching)
  useEffect(() => {
    let isMounted = true;

    const loadTagsAndTemplates = async () => {
      setIsLoadingTemplates(true);
      const startTime = performance.now();

      try {
        console.log('[HomePage] Loading tags and templates...');

        const tags = await FirebaseService.getAllTags();
        if (!isMounted) return;

        setAvailableTags(tags);

        if (tags.length > 0) {
          const templates = await FirebaseService.getTemplatesWithTagAccess(tags, currentUser);
          if (isMounted) {
            setAvailableTemplates(templates);
          }
        }

        console.log(`[HomePage] Tags and templates loaded in ${(performance.now() - startTime).toFixed(0)}ms`);
      } catch (error) {
        console.error('Error loading tags/templates:', error);
        if (isMounted) {
          setAvailableTemplates([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingTemplates(false);
        }
      }
    };

    loadTagsAndTemplates();

    return () => { isMounted = false; };
  }, [currentUser]);

  // Logout Handler
  const handleLogout = useCallback(async () => {
    try {
      await FirebaseService.logout();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, []);

  const handleViewAssistant = useCallback((assistant: AssistantDisplay) => {
    const url = `/assistant/${assistant.assistantId}`;
    window.open(url, '_blank');
  }, []);

  const handleCreateAssistant = useCallback((templateId: string) => {
    window.location.href = `/create?template=${templateId}`;
  }, []);

  // Show loading state
  const isLoading = isCacheLoading || isLoadingTemplates;
  if (isLoading && userState !== 'not-logged-in') {
     return <div className="fixed inset-0 bg-background flex items-center justify-center">Loading...</div>;
  }

  return (
    <SidebarProvider>
      <main className="flex-1">
        <LandingPage
          userState={userState}
          currentUser={currentUser}
          onShowLogin={() => setShowLoginDialog(true)}
          publicAssistants={publicAssistants}
          userAssistants={userAssistants}
          onViewAssistant={handleViewAssistant}
          templates={availableTemplates}
          onCreateAssistant={handleCreateAssistant}
          onLogout={handleLogout}
        />
      </main>
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
      />
    </SidebarProvider>
  );
}